import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { academyAdminAPI } from '../../lib/academyApi';
import { Card, CardContent } from '../../components/ui/card';
import {
  LayoutDashboard, BookOpen, FolderTree, Users, ClipboardList,
  CreditCard, Settings, GraduationCap, ChevronRight, Video, Calendar, Award, BarChart3,
} from 'lucide-react';

const AcademieBrand = () => (
  <span className="font-heading font-bold text-lg">
    PEY <span>a</span><span style={{color:'#F9B438'}}>CAD</span><span>emie</span>
  </span>
);

const navItems = [
  { path: '/academy', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
  { path: '/academy/formations', icon: BookOpen, label: 'Formations' },
  { path: '/academy/categories', icon: FolderTree, label: 'Categories' },
  { path: '/academy/instructeurs', icon: Users, label: 'Instructeurs' },
  { path: '/academy/sessions', icon: Calendar, label: 'Sessions' },
  { path: '/academy/inscriptions', icon: ClipboardList, label: 'Inscriptions' },
  { path: '/academy/certificats', icon: Award, label: 'Certificats' },
  { path: '/academy/paiements', icon: CreditCard, label: 'Paiements' },
  { path: '/academy/analytique', icon: BarChart3, label: 'Analytique' },
  { path: '/academy/parametres', icon: Settings, label: 'Parametres' },
  { path: '/academy/lms', icon: Video, label: 'LMS Futur', badge: 'Bientot' },
];

const AcademyLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'administrateur' && user.role !== 'employe') {
      navigate('/portail');
    }
  }, [user, navigate]);

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="academy-admin">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-[#1D1D1B] text-white flex-shrink-0 hidden lg:block">
          <div className="p-6 border-b border-white/10">
            <Link to="/academy" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>
                <GraduationCap className="w-5 h-5 text-[#1D1D1B]" />
              </div>
              <AcademieBrand />
            </Link>
          </div>
          <nav className="p-4 space-y-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive(item.path, item.exact) ? 'bg-white/10 text-[#FFD537]' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`academy-nav-${item.label.toLowerCase().replace(/\s/g,'-')}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {item.badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F9B438]/20 text-[#FFD537]">{item.badge}</span>}
              </Link>
            ))}
          </nav>
          <div className="p-4 mt-auto border-t border-white/10">
            <Link to="/cms" className="flex items-center gap-2 px-3 py-2 text-white/40 hover:text-white text-sm transition-colors">
              <ChevronRight className="w-4 h-4" /> Retour au CMS
            </Link>
            <Link to="/portail" className="flex items-center gap-2 px-3 py-2 text-white/40 hover:text-white text-sm transition-colors">
              <ChevronRight className="w-4 h-4" /> Retour au portail
            </Link>
          </div>
        </aside>
        {/* Main */}
        <main className="flex-1 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// Dashboard overview
export const AcademyDashboard = () => {
  const [stats, setStats] = useState({ trainings: 0, categories: 0, instructors: 0, registrations: 0, sessions: 0, certificates: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      academyAdminAPI.listTrainings(),
      academyAdminAPI.listCategories(),
      academyAdminAPI.listInstructors(),
      academyAdminAPI.listRegistrations(),
      academyAdminAPI.listSessions(),
      academyAdminAPI.listCertificates(),
    ]).then(([t, c, i, r, s, cert]) => {
      setStats({ trainings: t.data.length, categories: c.data.length, instructors: i.data.length, registrations: r.data.length, sessions: s.data.length, certificates: cert.data.length });
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const cards = [
    { label: 'Formations', value: stats.trainings, icon: BookOpen, link: '/academy/formations', color: '#F9B438' },
    { label: 'Sessions', value: stats.sessions, icon: Calendar, link: '/academy/sessions', color: '#0ea5e9' },
    { label: 'Inscriptions', value: stats.registrations, icon: ClipboardList, link: '/academy/inscriptions', color: '#a855f7' },
    { label: 'Certificats', value: stats.certificates, icon: Award, link: '/academy/certificats', color: '#22c55e' },
    { label: 'Instructeurs', value: stats.instructors, icon: Users, link: '/academy/instructeurs', color: '#f97316' },
    { label: 'Categories', value: stats.categories, icon: FolderTree, link: '/academy/categories', color: '#3b82f6' },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-[#1D1D1B] mb-1">Tableau de bord</h1>
        <p className="text-neutral-500">Vue d'ensemble de PEY aCADemie</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map(c => (
          <Link key={c.label} to={c.link}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${c.color}20` }}>
                    <c.icon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1D1D1B]">{loading ? '-' : c.value}</p>
                <p className="text-sm text-neutral-500">{c.label}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AcademyLayout;
